/**
 * Flowlytics Pixel SDK - Configuration Management
 * Read-Only Architecture - No cookies created, only read existing ones
 */

class FlowlyticsConfig {
  constructor(options = {}) {
    // Required configuration
    this.apiKey = options.apiKey || null;
    this.endpoint =
      options.endpoint ||
      "https://flowlytics.synaptica-solution.com/api/pixel/events";

    // Optional security configuration
    this.secretKey = options.secretKey || null; // For HMAC signature validation

    // Optional configuration
    this.debug = options.debug || false;
    this.sessionTimeout = options.sessionTimeout || 30 * 60 * 1000; // 30 minutes
    this.batchSize = options.batchSize || 10; // Accumulate events, one request per batch
    this.batchInterval = options.batchInterval || 3000; // 3 seconds

    // Read-Only mode (never write cookies)
    this.readOnlyMode = true;

    // Conversion tracking bridge — fires platform pixels client-side
    this.conversions = options.conversions || null;

    // Whether the SDK may show its own consent banner: false, or an object with
    // { mode, theme, position, labels, privacyPolicyUrl }. Read by init.js.
    // Until 2026-09-10 it was never copied here, so `consentBanner: false` in
    // a snippet had no effect and the banner showed anyway.
    this.consentBanner = options.consentBanner;

    // Feature flags
    this.features = {
      cookieTracking: options.cookieTracking !== false,
      deviceFingerprint: options.deviceFingerprint !== false,
      gtmIntegration: options.gtmIntegration !== false,
      sessionTracking: options.sessionTracking !== false,
      consentMode: options.consentMode !== false,
    };

    // Platform cookie mappings (Read-Only) - 50+ platforms
    this.platformCookies = {
      // Major Advertising Platforms
      google: [
        "_ga",
        "_gid",
        "_gac",
        "_gcl_au",
        "_gcl_aw",
        "_gcl_dc",
        "__utma",
        "__utmz",
        "__utmb",
        "__utmc",
        "__utmv",
      ],
      facebook: ["_fbp", "_fbc", "fr", "datr", "sb"],
      tiktok: ["_ttp", "tt_appInfo", "tt_pixel_session_index", "ttclid"],
      twitter: ["personalization_id", "guest_id", "twclid", "muc_ads"],
      pinterest: ["_epik", "_pin_unauth"],
      snapchat: ["sc_at", "_scid"],
      microsoft: ["MUID", "_uetsid", "_uetvid", "MUIDB", "_clck", "_clsk"],
      reddit: ["_rdt_uuid"],
      linkedin: [
        "li_fat_id",
        "lidc",
        "bcookie",
        "UserMatchHistory",
        "li_sugr",
        "AnalyticsSyncHistory",
      ],
      taboola: ["t_gid", "t_pt_gid"],
      outbrain: ["obuid"],
      criteo: ["cto_bundle"],
      adroll: ["__adroll_fpc"],
      quora: ["m-b", "m-uid"],
      bing: ["MUID", "MUIDB"],

      // Amazon
      amazon: [
        "ad-id",
        "session-id",
        "session-token",
        "x-amz-id-1",
        "amzn-bid",
      ],

      // Yahoo/Verizon
      yahoo: ["A1", "A3", "B", "Y", "GUC", "ygp"],

      // Adobe Analytics/Experience Cloud
      adobe: ["s_vi", "s_fid", "s_cc", "s_sq", "mbox", "aam_uuid", "demdex"],

      // Salesforce/Pardot
      salesforce: ["_sfmc_", "_kuid_"],

      // Marketo
      marketo: ["_mkto_trk"],

      // Klaviyo
      klaviyo: ["__kla_id", "klaviyo_viewed"],

      // ActiveCampaign
      activecampaign: ["cac", "vl"],

      // Session Recording - Hotjar
      hotjar: [
        "_hjid",
        "_hjFirstSeen",
        "_hjIncludedInPageviewSample",
        "_hjIncludedInSessionSample",
      ],

      // Session Recording - FullStory
      fullstory: ["fs_uid"],

      // Session Recording - Heap
      heap: ["_hp2_id"],

      // Session Recording - Lucky Orange
      lucky_orange: ["__lo_u", "__lo_v"],

      // Session Recording - Crazy Egg
      crazy_egg: ["_ceir", "_ce.s"],

      // Session Recording - Mouseflow
      mouseflow: ["mf_user", "mf_session"],

      // Session Recording - Smartlook
      smartlook: ["SL_C_", "SL_GWPT_Show_Hide_tmp"],

      // Session Recording - LogRocket
      logrocket: ["_lr_env_src", "_lr_id"],

      // Ad Networks - Affiliate
      awin: ["awc", "_aw_m_"],
      impact: ["irclickid"],
      partnerize: ["_pz_"],
      rakuten: ["rmStore"],
      cj: ["cje"],
      shareasale: ["SASESSIONID"],
      tradedoubler: ["TRADEDOUBLER"],

      // Ad Networks - Publishers
      mediavine: ["_mv_"],
      ezoic: ["ezCMPCookieConsent", "ezouid"],
      adsense: ["__gads", "__gpi"],

      // E-commerce - Shopify
      shopify: ["_y", "_s", "secure_customer_sig", "cart_ts"],

      // E-commerce - WooCommerce
      woocommerce: ["woocommerce_cart_hash", "woocommerce_items_in_cart"],

      // E-commerce - Magento
      magento: ["mage-cache-storage", "form_key", "PHPSESSID"],

      // E-commerce - BigCommerce
      bigcommerce: ["_bcvm_vid_"],

      // E-commerce - PrestaShop
      prestashop: [], // Dynamic: PrestaShop-*

      // E-commerce - OpenCart
      opencart: ["OCSESSID"],

      // Email Marketing - Mailchimp
      mailchimp: ["mailchimp_landing_site", "mc_cid", "mc_eid"],

      // Email Marketing - Sendinblue/Brevo
      sendinblue: ["sib_cuid", "__sibSession"],

      // Email Marketing - ConvertKit
      convertkit: ["ck_subscriber_id"],

      // Email Marketing - Drip
      drip: ["_drip_client", "_dripv_visitor"],

      // Email Marketing - Others
      constant_contact: ["ctct_"],
      aweber: ["aweber_"],
      getresponse: ["_gr_"],
      campaign_monitor: ["_cm_"],

      // CRM/Marketing Automation
      hubspot: ["hubspotutk", "__hstc", "__hssc", "__hsfp"],
      intercom: [], // Dynamic: intercom-id-*
      drift: ["drift_aid"],

      // Analytics Platforms
      segment: ["ajs_anonymous_id", "ajs_user_id"],
      amplitude: [], // Dynamic: amplitude_id_*
      mixpanel: [], // Dynamic: mp_*_mixpanel
    };

    this.validate();
  }

  validate() {
    if (!this.apiKey) {
      throw new Error("Flowlytics: API Key is required");
    }

    if (!/^[a-zA-Z0-9_-]{20,}$/.test(this.apiKey)) {
      throw new Error("Flowlytics: Invalid API Key format");
    }

    if (this.debug) {
      console.log("[Flowlytics] Configuration loaded:", {
        endpoint: this.endpoint,
        readOnlyMode: this.readOnlyMode,
        hmacEnabled: !!this.secretKey,
        features: this.features,
      });
    }
  }

  get(key) {
    return this[key];
  }

  set(key, value) {
    if (key === "readOnlyMode") {
      console.warn("[Flowlytics] Read-Only mode cannot be disabled");
      return;
    }
    this[key] = value;
  }
}

export default FlowlyticsConfig;
